import React, { useState } from 'react';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../../../CommonComponents/OPL/Button/Button';
import { Icon } from '../../../../../../../../CommonComponents/OPL/Icon/Icon';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { Tooltip } from '../../../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { PieChart } from '../../../../../../../../CommonComponents/OPL/charts/PieChart/PieChart';
import { PieChartLoading } from '../../../../../../../../CommonComponents/OPL/charts/PieChart/PieChart.loading';
import { MESSAGES } from '../../../../../../../../CommonConstants';
import { formatPercentage } from '../../../../../../../../CommonUtils/formatNumber';
import { PdfPageWrapper } from '../../../../../../../../PDFRenderer/PDFRenderer.styles';
import { Shimmer } from '../../../../../../../../CommonComponents/OPL/Shimmer/Shimmer';
import { theme as baseTheme } from '../../../../../../../../theme/theme';
import { SocialSecurityStrategy } from '../../api/useAnnualBenefitsSummaryQuery';
import { useApplySocialSecurityStrategy } from '../../useApplySocialSecurityStrategy';
import { useSocialSecurity } from '../../useSocialSecurity';
import { useSocialSecuritySuccessProbabilities } from '../../useSocialSecuritySuccessProbabilities';
import { BenefitsModal } from '../BenefitsModal/BenefitsModal';
import { StickyFirstColumnTable } from './SocialSecurityTable.style';

interface Props {
  data: NonNullable<ReturnType<typeof useSocialSecurity>['data']>;
  successProbabilitiesData: ReturnType<typeof useSocialSecuritySuccessProbabilities>['data'];
  // Per-strategy loading/error states so each Probability of Success cell can update independently.
  loadingStates: boolean[];
  errors: (Error | null)[];
  isPrint?: boolean;
}

export const SocialSecurityTable = ({
  data,
  successProbabilitiesData,
  loadingStates,
  errors,
  isPrint = false,
}: Props) => {
  const [benefitsModalStrategy, setBenefitsModalStrategy] = useState<SocialSecurityStrategy | null>(
    null,
  );

  const {
    apply: onApplyStrategy,
    isUpdatingTable,
    applyingStrategy,
    isApplyVisible,
  } = useApplySocialSecurityStrategy();

  const isStrategyApplied = (strategy: SocialSecurityStrategy) =>
    data.appliedStrategies?.includes(strategy) ?? false;

  const isStrategyApplicable = (strategy: SocialSecurityStrategy) =>
    data.applicableStrategies?.includes(strategy) ?? false;

  const hasApplyRow =
    (data.appliedStrategies?.length ?? 0) > 0 || (data.applicableStrategies?.length ?? 0) > 0;

  const probabilityValues = successProbabilitiesData?.ProbabilityOfSuccess ?? [];

  const hasSpouse = data.spouseName != null;
  const everyoneReceivingBenefits = hasSpouse
    ? data.principalIsCurrentlyReceivingBenefits && data.spouseIsCurrentlyReceivingBenefits
    : data.principalIsCurrentlyReceivingBenefits;
  const anyoneReceivingBenefits =
    data.principalIsCurrentlyReceivingBenefits || data.spouseIsCurrentlyReceivingBenefits;

  const firstYearBenefitLabel = everyoneReceivingBenefits
    ? 'Current Year Benefit'
    : anyoneReceivingBenefits
    ? 'Current/First Year Benefit'
    : 'First Year Benefit';

  // Sub-label for every row whose cells are formatted as "principal / spouse".
  // Print stacks each name on its own line so it lines up with the stacked cell values.
  const namesLabel = isPrint ? (
    <>
      {data.principalName}
      {hasSpouse && (
        <>
          <br />
          {data.spouseName}
        </>
      )}
    </>
  ) : (
    `(${data.principalName}${hasSpouse ? ` / ${data.spouseName}` : ''})`
  );

  // Computed here, not inside the css prop. The styled-components Babel plugin
  // hoists that callback to module scope, where isPrint is not defined.
  const headerCellPadding = isPrint
    ? `${baseTheme.space['4']}px ${baseTheme.space['8']}px`
    : `${baseTheme.space['8']}px ${baseTheme.space['16']}px`;

  return (
    <>
      <Box>
        <PdfPageWrapper>
          <StickyFirstColumnTable
            variant="bordered"
            columnSpacing="small"
            edgePadding={isPrint ? 8 : 16}
            $isPrint={isPrint}
          >
            <table>
              <thead>
                <tr>
                  <td> </td>
                  {data.headerNames.map(({ label, value }) => (
                    <td
                      key={value}
                      css={{ padding: headerCellPadding }}
                    >
                      <Box
                        display="flex"
                        css={({ theme }) => ({ gap: theme.space['4'] })}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text
                          textAlign="left"
                          fontWeight={500}
                          css={{
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                          }}
                        >
                          {label}
                        </Text>

                        {value === 'AtFra' && (
                          <Tooltip content="Full Retirement Age (FRA): The age at which you become eligible to receive 100% of your Social Security retirement benefit based on your earnings record. FRA is determined by your birth year. Claiming Social Security before FRA permanently reduces your monthly benefit, while delaying beyond FRA can increase it up to age 70.">
                            <Icon
                              className="far fa-info-circle"
                              color="neutral.500"
                              fontSize="small"
                              ml="4"
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </td>
                  ))}
                </tr>

                <tr>
                  <th colSpan={6}>
                    <Text textAlign="left" fontWeight={600}>
                      Category
                    </Text>
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>Probability of Success</td>
                  {data.headerNames.map(({ value: strategy }, index) => {
                    const isLoading = loadingStates[index] || isUpdatingTable;
                    const cellError = errors[index];
                    const value = probabilityValues[index];

                    if (cellError) {
                      return (
                        <td key={index}>
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            width="100%"
                            height="64px"
                          >
                            <Text variant="tabular" textAlign="center" fontSize="small">
                              {cellError.message || MESSAGES.GENERIC_ERROR}
                            </Text>
                          </Box>
                        </td>
                      );
                    }

                    if (isLoading) {
                      return (
                        <td key={index}>
                          <Box display="flex" justifyContent="center" width="100%">
                            <Box width="64px" height="64px">
                              <PieChartLoading />
                            </Box>
                          </Box>
                        </td>
                      );
                    }

                    if (value == null) {
                      return (
                        <td key={index}>
                          <Text variant="tabular" textAlign="center">
                            -
                          </Text>
                        </td>
                      );
                    }

                    return (
                      <td key={index}>
                        <Box display="flex" justifyContent="center" width="100%">
                          <Box width="64px" height="64px">
                            <PieChart
                              series={[
                                {
                                  data: [
                                    {
                                      name: 'Success',
                                      y: value,
                                      color:
                                        value >= 0.7
                                          ? baseTheme.colors.success[500]
                                          : baseTheme.colors.error[600],
                                    },
                                    {
                                      name: 'Failure',
                                      y: 1 - value,
                                      color: baseTheme.colors.neutral[200],
                                    },
                                  ],
                                },
                              ]}
                              title={formatPercentage(value, { decimalPlaces: 0 })}
                              customSettings={{
                                title: {
                                  style: {
                                    fontSize: `${baseTheme.fontSizes.small}px`,
                                  },
                                },
                                plotOptions: {
                                  pie: {
                                    innerSize: '75%',
                                  },
                                  ...(isPrint && {
                                    series: {
                                      animation: false,
                                    },
                                  }),
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </td>
                    );
                  })}
                </tr>

                <tr>
                  <td>
                    Benefit Start Age
                    <br />
                    <Text fontSize="small" mt="4">
                      {namesLabel}
                    </Text>
                  </td>
                  {data.benefitStartAge.map((value, index) => {
                    if (isUpdatingTable) {
                      return (
                        <td key={index}>
                          <Box display="flex" justifyContent="center">
                            <Shimmer width="80%" height={24} />
                          </Box>
                        </td>
                      );
                    }

                    return (
                      <td key={index}>
                        <Text variant="tabular" textAlign="center">
                          <SlashSeparatedValue value={value} isPrint={isPrint} />
                        </Text>
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td>
                    {firstYearBenefitLabel}
                    <br />
                    <Text fontSize="small" mt="4">
                      {namesLabel}
                    </Text>
                  </td>
                  {data.firstYearBenefit.map((value, index) => {
                    if (isUpdatingTable) {
                      return (
                        <td key={index}>
                          <Box display="flex" justifyContent="center">
                            <Shimmer width="80%" height={24} />
                          </Box>
                        </td>
                      );
                    }

                    return (
                      <td key={index}>
                        <Text variant="tabular" textAlign="center">
                          <SlashSeparatedValue value={value} isPrint={isPrint} />
                        </Text>
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td>
                    Total Lifetime Benefit
                    <br />
                    <Text fontSize="small" mt="4">
                      {namesLabel}
                    </Text>
                  </td>
                  {data.totalLifetimeBenefit.map((value, index) => {
                    if (isUpdatingTable) {
                      return (
                        <td key={index} css={{ textAlign: 'center' }}>
                          <Box display="flex" justifyContent="center">
                            <Shimmer width="80%" height={24} />
                          </Box>
                        </td>
                      );
                    }

                    return (
                      <td key={index} css={{ textAlign: 'center' }}>
                        <Button
                          variant="link"
                          onClick={() => {
                            setBenefitsModalStrategy(data.headerNames[index].value);
                          }}
                        >
                          <SlashSeparatedValue value={value} isPrint={isPrint} />
                        </Button>
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td>Retirement Assets at End of Plan</td>
                  {data.retirementAssetsAtEndOfPlan.map((value, index) => {
                    if (isUpdatingTable) {
                      return (
                        <td key={index}>
                          <Box display="flex" justifyContent="center">
                            <Shimmer width="80%" height={24} />
                          </Box>
                        </td>
                      );
                    }

                    return (
                      <td key={index}>
                        <Text variant="tabular" textAlign="center">
                          {value}
                        </Text>
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td>Net Worth at End of Plan</td>
                  {data.netWorthAtEndOfPlan.map((value, index) => {
                    if (isUpdatingTable) {
                      return (
                        <td key={index}>
                          <Box display="flex" justifyContent="center">
                            <Shimmer width="80%" height={24} />
                          </Box>
                        </td>
                      );
                    }

                    return (
                      <td key={index}>
                        <Text variant="tabular" textAlign="center">
                          {value}
                        </Text>
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td>
                    <Tooltip content="The break-even age represents the age in which the cumulative benefits for a strategy would surpass the As Soon As Possible strategy. It is the age after which delaying benefits would lead to an increase in total lifetime benefits.">
                      <Box
                        display="flex"
                        alignItems="center"
                        css={({ theme }) => ({ gap: theme.space['4'] })}
                      >
                        <Text variant="tabular">
                          Break-even Age
                          <br />
                          <Text fontSize="small" mt="4">
                            {namesLabel}
                          </Text>
                        </Text>
                        <Icon
                          className="far fa-info-circle"
                          color="neutral.500"
                          fontSize="small"
                          ml="4"
                        />
                      </Box>
                    </Tooltip>
                  </td>
                  {data.breakEvenAge.map((value, index) => {
                    if (isUpdatingTable) {
                      return (
                        <td key={index}>
                          <Box display="flex" justifyContent="center">
                            <Shimmer width="80%" height={24} />
                          </Box>
                        </td>
                      );
                    }

                    return (
                      <td key={index}>
                        <Text variant="tabular" textAlign="center">
                          <SlashSeparatedValue value={value} isPrint={isPrint} />
                        </Text>
                      </td>
                    );
                  })}
                </tr>
                {!isPrint && isApplyVisible && hasApplyRow && (
                  <tr>
                    <td />
                    {data.headerNames.map(({ value }) => {
                      if (
                        isStrategyApplied(value) &&
                        !(isUpdatingTable && applyingStrategy === value)
                      ) {
                        return (
                          <td
                            key={value}
                            style={{ textAlign: 'center', paddingLeft: 0, paddingRight: 0 }}
                          >
                            <Button size="small" disabled leftIcon="fas fa-check">
                              Applied
                            </Button>
                          </td>
                        );
                      }

                      if (!isStrategyApplicable(value)) {
                        return (
                          <td
                            key={value}
                            style={{ textAlign: 'center', paddingLeft: 0, paddingRight: 0 }}
                          />
                        );
                      }

                      return (
                        <td
                          key={value}
                          style={{ textAlign: 'center', paddingLeft: 0, paddingRight: 0 }}
                        >
                          <Button
                            size="small"
                            disabled={isUpdatingTable && applyingStrategy !== value}
                            isLoading={isUpdatingTable && applyingStrategy === value}
                            onClick={() => onApplyStrategy(value)}
                          >
                            Apply this Scenario
                          </Button>
                        </td>
                      );
                    })}
                  </tr>
                )}
              </tbody>
            </table>
          </StickyFirstColumnTable>
        </PdfPageWrapper>
      </Box>

      {!isPrint && benefitsModalStrategy && (
        <BenefitsModal
          strategy={benefitsModalStrategy}
          onClose={() => setBenefitsModalStrategy(null)}
        />
      )}
    </>
  );
};

/** Print lays "principal / spouse" on two lines so the pair fits a PDF column. */
const SlashSeparatedValue = ({
  value,
  isPrint,
}: {
  value: string | null | undefined;
  isPrint: boolean;
}) => {
  const text = value ?? '-';
  if (!isPrint) {
    return <>{text}</>;
  }

  const parts = text.split(' / ');
  if (parts.length < 2) {
    return <>{text}</>;
  }

  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={`${part}-${index}`}>
          {index > 0 && <br />}
          {part}
        </React.Fragment>
      ))}
    </>
  );
};
